import React, { useEffect, useState, useMemo } from 'react';
import {
  DollarSign,
  Users,
  TrendingUp,
  AlertCircle,
  Calendar,
  FileText,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Send,
  Settings,
  AlertTriangle,
  BarChart3,
  Download,
  Shield,
  Info,
  RefreshCw,
  ChevronDown,
  Search
} from 'lucide-react';
import { PatientService } from '../../services/PatientService';
import { AlertService, PackageAlert } from '../../services/AlertService';
import { InvoiceService } from '../../services/InvoiceService';
import { UnifiedPatient, FinancialMetrics } from '../../types/unified-financial';
import PatientFinancialModal from '../../components/finance/PatientFinancialModal';
import AlertsPanel from '../../components/finance/AlertsPanel';
import InvoicesPanel from '../../components/finance/InvoicesPanel';
import ProfessionalPaymentPanel from '../../components/finance/ProfessionalPaymentPanel';
import { useLocation, useNavigate } from 'react-router-dom';
import RenewalDecisionsPanel from '../../components/finance/RenewalDecisionsPanel';
import { RenewalDecisionService, RenewalDecisionTask, RenewalDecisionAction } from '../../services/RenewalDecisionService';
import { useAuth } from '../../context/AuthContext';
import { matchesPatientSearch } from '../../utils/patientSearch';

interface DashboardState {
  patients: UnifiedPatient[];
  metrics: FinancialMetrics | null;
  selectedPatient: UnifiedPatient | null;
  loading: boolean;
  error: string | null;
}

type SortOption = 'name-asc' | 'name-desc' | 'status-active' | 'status-inactive';

export default function NewFinancialDashboard() {
  const { user, selectedClient } = useAuth();
  const clientId = selectedClient?.clientId;
  const isSuperAdmin = user?.userGlobalRole === 'superAdmin';
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<DashboardState>({
    patients: [],
    metrics: null,
    selectedPatient: null,
    loading: true,
    error: null,
  });

  const [activeTab, setActiveTab] = useState<'overview' | 'patients' | 'invoices' | 'professionals'>('overview');
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [alerts, setAlerts] = useState<PackageAlert[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [invoiceStats, setInvoiceStats] = useState({ total: 0, pending: 0, overdue: 0 });
  const [renewalQueue, setRenewalQueue] = useState<RenewalDecisionTask[]>([]);
  const [pendingRenewDecision, setPendingRenewDecision] = useState<{ patientId: string; decisionId: string } | null>(null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [alertActionLoading, setAlertActionLoading] = useState(false);
  const [showAlertsDetails, setShowAlertsDetails] = useState(false);
  const [showRenewalDetails, setShowRenewalDetails] = useState(false);

  const requestedInvoicesDueMonth = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const dueMonth = params.get('dueMonth') ?? undefined;
    if (!dueMonth) return undefined;
    return /^\d{4}-\d{2}$/.test(dueMonth) ? dueMonth : undefined;
  }, [location.search]);

  const requestedPatientId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('patientId') || undefined;
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');
    if (
      requestedTab === 'overview' ||
      requestedTab === 'patients' ||
      requestedTab === 'invoices' ||
      requestedTab === 'professionals'
    ) {
      setActiveTab(requestedTab);
    }
  }, [location.search]);

  const formatDate = (value?: string) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleDateString('pt-BR');
  };

  // Carrega dados iniciais
  useEffect(() => {
    if (!clientId) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Tenant não selecionado. Selecione o tenant para carregar o dashboard financeiro.'
      }));
      setInvoiceStats({ total: 0, pending: 0, overdue: 0 });
      setRenewalQueue([]);
      setAlerts([]);
      return;
    }
    loadDashboardData();
    loadInvoiceStats();
    // Carregar preferência de ordenação do localStorage
    const savedSort = localStorage.getItem('patientSortPreference') as SortOption;
    if (savedSort) {
      setSortBy(savedSort);
    }
  }, [clientId]);

  useEffect(() => {
    if (!requestedPatientId || state.patients.length === 0) return;

    const selectedPatient = state.patients.find((patient) => patient.id === requestedPatientId);
    if (selectedPatient) {
      setState((prev) => ({ ...prev, selectedPatient }));
    }
  }, [requestedPatientId, state.patients]);

  // Carregar estatísticas de faturas
  const loadInvoiceStats = async () => {
    try {
      if (!clientId) {
        setInvoiceStats({ total: 0, pending: 0, overdue: 0 });
        return;
      }

      const allInvoices = await InvoiceService.getAllInvoices({ clientId });
      const pending = allInvoices.filter(inv => inv.status === 'open' || inv.status === 'pending').length;
      const overdue = allInvoices.filter(inv => inv.status === 'overdue').length;
      setInvoiceStats({
        total: allInvoices.length,
        pending,
        overdue
      });
    } catch (error) {
      console.error('Erro ao carregar estatísticas de faturas:', error);
    }
  };

  // Salvar preferência de ordenação
  useEffect(() => {
    localStorage.setItem('patientSortPreference', sortBy);
  }, [sortBy]);

  const loadDashboardData = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      if (!clientId) {
        throw new Error('Tenant não selecionado. Selecione o tenant para carregar os dados financeiros.');
      }

      const currentMonth = new Date().toISOString().substring(0, 7); // YYYY-MM

      let patients = await PatientService.getAllPatients(clientId);
      patients = await RenewalDecisionService.ensurePendingDecisions(patients, currentMonth);
      const decisions = RenewalDecisionService.collectPendingDecisions(patients);
      setRenewalQueue(decisions);

      const metrics = await PatientService.calculateMonthlyMetrics(currentMonth, clientId);
      
      // Calcular alertas
      const packageAlerts = AlertService.calculatePackageAlerts(patients);
      setAlerts(packageAlerts);

      setState(prev => ({
        ...prev,
        patients,
        metrics,
        loading: false,
      }));
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message,
        loading: false,
      }));
      setRenewalQueue([]);
    }
  };

  // Função de ordenação de pacientes
  const sortPatients = (patients: UnifiedPatient[], sortOption: SortOption): UnifiedPatient[] => {
    const sorted = [...patients];

    switch (sortOption) {
      case 'name-asc':
        return sorted.sort((a, b) =>
          a.personalData.name.localeCompare(b.personalData.name, 'pt-BR')
        );

      case 'name-desc':
        return sorted.sort((a, b) =>
          b.personalData.name.localeCompare(a.personalData.name, 'pt-BR')
        );

      case 'status-active':
        return sorted.sort((a, b) => {
          const aActive = a.financialProfile?.active === true ? 1 : 0;
          const bActive = b.financialProfile?.active === true ? 1 : 0;
          if (bActive - aActive !== 0) return bActive - aActive;
          return a.personalData.name.localeCompare(b.personalData.name, 'pt-BR');
        });

      case 'status-inactive':
        return sorted.sort((a, b) => {
          const aActive = a.financialProfile?.active === true ? 1 : 0;
          const bActive = b.financialProfile?.active === true ? 1 : 0;
          if (aActive - bActive !== 0) return aActive - bActive;
          return a.personalData.name.localeCompare(b.personalData.name, 'pt-BR');
        });

      default:
        return sorted;
    }
  };

  const handleFinancialModalClose = () => {
    setShowFinancialModal(false);
    setPendingRenewDecision(null);
  };

  const executeDecisionAction = async (
    task: RenewalDecisionTask,
    action: RenewalDecisionAction,
    options?: { obitoDate?: string }
  ) => {
    try {
      setDecisionLoading(true);
      await RenewalDecisionService.resolveDecision({
        patientId: task.patient.id,
        decisionId: task.decision.decisionId,
        action,
        clientId: clientId || undefined,
        obitoDate: options?.obitoDate
      });
      await loadDashboardData();
    } catch (error: any) {
      alert(`Erro ao registrar decisão: ${error.message || error}`);
      console.error('Erro ao registrar decisão de renovação:', error);
    } finally {
      setDecisionLoading(false);
    }
  };

  const handleFinancialModalUpdated = async () => {
    if (pendingRenewDecision) {
      try {
        setDecisionLoading(true);
        await RenewalDecisionService.resolveDecision({
          patientId: pendingRenewDecision.patientId,
          decisionId: pendingRenewDecision.decisionId,
          action: 'renew',
          clientId: clientId || undefined
        });
      } catch (error: any) {
        alert(`Erro ao finalizar renovação: ${error.message || error}`);
        console.error('Erro ao finalizar renovação:', error);
      } finally {
        setDecisionLoading(false);
        setPendingRenewDecision(null);
      }
    }
    await loadDashboardData();
  };

  const handleRenewalDecisionAction = async (task: RenewalDecisionTask, action: RenewalDecisionAction) => {
    if (decisionLoading) return;

    if (action === 'renew') {
      setPendingRenewDecision({ patientId: task.patient.id, decisionId: task.decision.decisionId });
      openFinancialModal(task.patient);
      return;
    }

    if (action === 'individual_sessions') {
      const confirm = window.confirm(
        `Deseja converter o paciente ${task.patient.personalData.name} para sessões avulsas a partir deste ciclo?`
      );
      if (!confirm) return;
      await executeDecisionAction(task, action);
      return;
    }

    if (action === 'discharge') {
      const confirm = window.confirm(
        `Confirma a alta do paciente ${task.patient.personalData.name}? Todos os agendamentos futuros deverão ser revisados manualmente.`
      );
      if (!confirm) return;
      await executeDecisionAction(task, action);
      return;
    }

    if (action === 'deceased') {
      const defaultDate = new Date().toISOString().split('T')[0];
      const input = window.prompt(
        `Informe a data do óbito de ${task.patient.personalData.name} (YYYY-MM-DD ou DD/MM/AAAA):`,
        defaultDate
      );
      if (!input) return;
      await executeDecisionAction(task, action, { obitoDate: input });
    }
  };

  const handleExtendPackageDeadline = async (alertItem: PackageAlert) => {
    if (alertActionLoading) return;

    const patient = state.patients.find(p => p.id === alertItem.patientId);
    if (!patient?.financialProfile?.packageInfo) {
      window.alert("Não foi possível localizar o pacote do paciente.");
      return;
    }

    const defaultExtra = "7";
    const input = window.prompt(
      `Informe quantos dias deseja adicionar ao prazo do pacote de ${patient.personalData.name}:`,
      defaultExtra
    );
    if (!input) return;

    const extraDays = parseInt(input, 10);
    if (Number.isNaN(extraDays) || extraDays <= 0) {
      window.alert("Informe um número válido de dias.");
      return;
    }

    try {
      setAlertActionLoading(true);

      const pkg = patient.financialProfile.packageInfo;
      // Corrigido para os novos nomes de propriedade
      const baseDate = pkg.packageRenewalDate ? new Date(pkg.packageRenewalDate) : new Date(pkg.packagePaymentDate);
      if (Number.isNaN(baseDate.getTime())) {
        throw new Error("Data de renovação inválida.");
      }

      baseDate.setDate(baseDate.getDate() + extraDays);
      baseDate.setHours(23, 59, 59, 999);

      // O PatientService.updateFinancialProfile foi refatorado e não aceita mais chaves aninhadas.
      // Precisamos enviar o objeto packageInfo completo.
      const updatedPackageInfo = { 
        ...pkg, 
        packageRenewalDate: baseDate.toISOString() 
      };

      const profileClientId = patient.financialProfile.clientId || clientId;
      if (!profileClientId) {
        throw new Error("ClientId não encontrado para atualizar perfil financeiro.");
      }

      await PatientService.updateFinancialProfile(patient.id, {
        clientId: profileClientId,
        active: patient.financialProfile.active,
        hasPackage: patient.financialProfile.hasPackage,
        individualSessionPrice: patient.financialProfile.individualSessionPrice,
        packageInfo: updatedPackageInfo,
      });

      window.alert(`Prazo estendido em ${extraDays} dia(s).`);
      await loadDashboardData();
    } catch (error: any) {      window.alert(`Erro ao estender prazo: ${error.message || error}`);
      console.error("Erro ao estender prazo do pacote:", error);
    } finally {
      setAlertActionLoading(false);
    }
  };

  // Lista de pacientes ordenada
  const sortedPatients = useMemo(() => {
    const matchingPatients = state.patients.filter((patient) =>
      matchesPatientSearch(
        patientSearchTerm,
        patient.personalData.name,
        [patient.personalData.cpf, patient.personalData.rg, patient.id],
      ),
    );
    return sortPatients(matchingPatients, sortBy);
  }, [patientSearchTerm, state.patients, sortBy]);

  // Dados computados
  const computedData = useMemo(() => {
    const activePatients = state.patients.filter(p => p.financialProfile?.active === true);
    const inactivePatients = state.patients.filter(p => p.financialProfile?.active !== true);
    const patientsWithPackage = activePatients.filter(p => p.financialProfile?.hasPackage === true);
    
    const openInvoices = state.patients.flatMap(p => 
      p.invoices.filter(inv => inv.status === 'open' || inv.status === 'pending')
    );
    
    const overdueInvoices = state.patients.flatMap(p => 
      p.invoices.filter(inv => inv.status === 'overdue')
    );

    // Calcular receita estimada do mês atual
    const currentMonth = new Date().toISOString().substring(0, 7);
    let estimatedRevenue = 0;
    let pendingRevenue = 0;
    let overdueRevenue = 0;

    activePatients.forEach(patient => {
      if (patient.financialProfile.hasPackage && patient.financialProfile.packageInfo) {
        estimatedRevenue += patient.financialProfile.packageInfo.packagePrice;
      } else {
        // Estimar baseado em sessões médias (4 por mês)
        estimatedRevenue += (patient.financialProfile.individualSessionPrice ?? 0) * 4;
      }
    });

    openInvoices.forEach(inv => {
      pendingRevenue += inv.total;
    });

    overdueInvoices.forEach(inv => {
      overdueRevenue += inv.total;
    });

    return {
      activePatients,
      inactivePatients,
      patientsWithPackage,
      openInvoices,
      overdueInvoices,
      estimatedRevenue,
      pendingRevenue,
      overdueRevenue,
    };
  }, [state.patients]);

  const handlePatientSelect = (patient: UnifiedPatient) => {
    setState(prev => ({ ...prev, selectedPatient: patient }));
  };

  const openFinancialModal = (patient: UnifiedPatient) => {
    handlePatientSelect(patient);
    setShowFinancialModal(true);
  };

  const handleAlertClick = (patientId: string) => {
    const patient = state.patients.find(p => p.id === patientId);
    if (patient) {
      openFinancialModal(patient);
    }
  };

  if (state.loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Carregando dashboard...</div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="text-red-800">Erro: {state.error}</div>
        <button 
          onClick={loadDashboardData}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Financeiro</h1>
          <p className="text-gray-600 mt-1">Sistema completo de gestão financeira integrado</p>
        </div>
        {isSuperAdmin && (
          <div className="flex gap-2">
            <button 
              onClick={loadDashboardData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <TrendingUp className="w-4 h-4" />
              Atualizar
            </button>
          </div>
        )}
      </div>

      {isSuperAdmin && (
        <div className="flex justify-end">
          <button
            onClick={() => navigate('/admin/data-quality')}
            className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-sm font-medium text-indigo-800 ring-1 ring-indigo-200 hover:bg-indigo-100"
          >
            <Shield className="h-4 w-4" />
            Qualidade de Dados
            <Eye className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Visão Geral', icon: BarChart3 },
            { id: 'patients', label: 'Pacientes', icon: Users },
            { id: 'invoices', label: 'Faturas', icon: FileText },
            { id: 'professionals', label: 'Repasse de Profissionais', icon: DollarSign },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === 'overview' && alerts.length > 0 && (
                <span className="bg-orange-500 text-white text-xs rounded-full px-2 py-0.5 ml-1">
                  {alerts.length}
                </span>
              )}
              {tab.id === 'invoices' && invoiceStats.total > 0 && (
                <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-0.5 ml-1">
                  {invoiceStats.total}
                </span>
              )}
              {tab.id === 'invoices' && invoiceStats.overdue > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 ml-1">
                  {invoiceStats.overdue}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Visão Geral */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Cards de Métricas Principais */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pacientes Ativos</p>
                  <p className="text-2xl font-bold text-green-600">{computedData.activePatients.length}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {state.patients.length} total
                  </p>
                </div>
                <Users className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Receita Estimada</p>
                  <p className="text-2xl font-bold text-blue-600">
                    R$ {computedData.estimatedRevenue.toFixed(0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Este mês</p>
                </div>
                <DollarSign className="w-8 h-8 text-blue-600" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Receita Pendente</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    R$ {computedData.pendingRevenue.toFixed(0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {computedData.openInvoices.length} faturas
                  </p>
                </div>
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Em Atraso</p>
                  <p className="text-2xl font-bold text-red-600">
                    R$ {computedData.overdueRevenue.toFixed(0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {computedData.overdueInvoices.length} vencidas
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
            </div>
          </div>

          {/* Painéis retráteis de atenção */}
          <div className="space-y-3">
            <div className="bg-white rounded-xl border shadow-sm">
              <button
                onClick={() => setShowAlertsDetails((prev) => !prev)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 rounded-xl transition-colors"
                aria-expanded={showAlertsDetails}
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                  <span className="font-semibold text-gray-900">Alertas de Vencimento</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-600">
                    {alerts.length} caso{alerts.length === 1 ? '' : 's'}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${showAlertsDetails ? 'rotate-180' : ''}`}
                  />
                </div>
              </button>
              {showAlertsDetails && (
                <div className="px-4 pb-4">
                  <AlertsPanel
                    alerts={alerts}
                    onPatientClick={handleAlertClick}
                    onExtendPackage={handleExtendPackageDeadline}
                    extendDisabled={alertActionLoading}
                  />
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border shadow-sm">
              <button
                onClick={() => setShowRenewalDetails((prev) => !prev)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 rounded-xl transition-colors"
                aria-expanded={showRenewalDetails}
              >
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-gray-900">Decisões de Renovação</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-600">
                    {renewalQueue.length} pendente{renewalQueue.length === 1 ? '' : 's'}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${showRenewalDetails ? 'rotate-180' : ''}`}
                  />
                </div>
              </button>
              {showRenewalDetails && (
                <div className="px-4 pb-4">
                  <RenewalDecisionsPanel
                    decisions={renewalQueue}
                    onAction={handleRenewalDecisionAction}
                    disabled={decisionLoading}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Métricas Detalhadas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Distribuição de Pacientes
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Com Pacote:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{computedData.patientsWithPackage.length}</span>
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-purple-600 h-2 rounded-full" 
                        style={{ 
                          width: `${(computedData.patientsWithPackage.length / Math.max(computedData.activePatients.length, 1)) * 100}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Sessões Avulsas:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {computedData.activePatients.length - computedData.patientsWithPackage.length}
                    </span>
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ 
                          width: `${((computedData.activePatients.length - computedData.patientsWithPackage.length) / Math.max(computedData.activePatients.length, 1)) * 100}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Inativos:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{computedData.inactivePatients.length}</span>
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gray-600 h-2 rounded-full" 
                        style={{ 
                          width: `${(computedData.inactivePatients.length / Math.max(state.patients.length, 1)) * 100}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Status dos Alertas
              </h3>
              <div className="space-y-3">
                {alerts.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    <p>Nenhum alerta ativo</p>
                    <p className="text-sm">Todos os pacotes estão em dia</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Críticos (≤3 dias):</span>
                      <span className="font-medium text-red-600">
                        {alerts.filter(a => a.alertType === 'critical').length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Atenção (≤7 dias):</span>
                      <span className="font-medium text-orange-600">
                        {alerts.filter(a => a.alertType === 'warning').length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Vencidos:</span>
                      <span className="font-medium text-red-800">
                        {alerts.filter(a => a.alertType === 'expired').length}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Aba Pacientes */}
      {activeTab === 'patients' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Lista de Pacientes */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border shadow-sm">
                <div className="p-4 border-b space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                      Pacientes ({patientSearchTerm.trim() ? `${sortedPatients.length} de ` : ''}{state.patients.length})
                    </h3>
                  </div>

                  {/* Controles de Ordenação */}
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="relative min-w-[240px] flex-1">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="search"
                        value={patientSearchTerm}
                        onChange={(event) => setPatientSearchTerm(event.target.value)}
                        placeholder="Buscar paciente por nome ou documento..."
                        className="w-full rounded-lg border border-gray-300 py-1.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <span className="text-sm text-gray-600 font-medium">Ordenar por:</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as SortOption)}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="name-asc">Nome (A-Z)</option>
                      <option value="name-desc">Nome (Z-A)</option>
                      <option value="status-active">Status (Ativo primeiro)</option>
                      <option value="status-inactive">Status (Inativo primeiro)</option>
                    </select>
                  </div>
                </div>
                <div className="divide-y max-h-96 overflow-y-auto">
                  {sortedPatients.map(patient => (
                    <div 
                      key={patient.id}
                      className={`p-4 hover:bg-gray-50 ${
                        state.selectedPatient?.id === patient.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div 
                          className="cursor-pointer flex-1"
                          onClick={() => handlePatientSelect(patient)}
                        >
                          <div className="font-medium">{patient.personalData.name}</div>
                          <div className="text-sm text-gray-500">
                            {patient.personalData.phone} • {patient.personalData.email}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {patient.financialProfile?.active === true ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                          {patient.financialProfile?.hasPackage && (
                            <Package className="w-4 h-4 text-purple-500" />
                          )}
                          {/* Indicador de alerta */}
                          {alerts.some(alert => alert.patientId === patient.id) && (
                            <AlertTriangle className="w-4 h-4 text-orange-500" />
                          )}
                          <button
                            onClick={() => openFinancialModal(patient)}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1"
                          >
                            <Settings className="w-3 h-3" />
                            Configurar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Detalhes do Paciente Selecionado */}
            <div>
              {state.selectedPatient ? (
                <div className="bg-white rounded-xl border shadow-sm">
                  <div className="p-4 border-b">
                    <h3 className="text-lg font-semibold">{state.selectedPatient.personalData.name}</h3>
                  </div>
                  <div className="p-4 space-y-4">
                    {/* Status */}
                    <div>
                      <div className="text-sm font-medium text-gray-600 mb-1">Status</div>
                      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        state.selectedPatient.financialProfile?.active === true
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {state.selectedPatient.financialProfile?.active === true ? 'Ativo' : 'Inativo'}
                      </div>
                    </div>

                    {/* Alerta */}
                    {alerts.some(alert => alert.patientId === state.selectedPatient?.id) && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-orange-600" />
                          <span className="text-sm font-medium text-orange-800">
                            Pacote próximo do vencimento
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Pacote */}
                    {state.selectedPatient.financialProfile.hasPackage && state.selectedPatient.financialProfile.packageInfo && (
                      <div>
                        <div className="text-sm font-medium text-gray-600 mb-2">Pacote Atual</div>
                        <div className="bg-gray-50 border rounded-lg p-3 space-y-2">
                          <p className="font-semibold text-gray-800">{state.selectedPatient.financialProfile.packageInfo.packageName}</p>
                          <div className="flex justify-between items-center text-sm">
                            <span className="font-bold text-blue-600">
                              {state.selectedPatient.financialProfile.packageInfo.packageSessionsUsed} / {state.selectedPatient.financialProfile.packageInfo.packageSessionsContracted}
                            </span>
                            <span className="text-gray-600">Sessões Utilizadas</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full"
                              style={{
                                width: `${(state.selectedPatient.financialProfile.packageInfo.packageSessionsUsed / state.selectedPatient.financialProfile.packageInfo.packageSessionsContracted) * 100}%`
                              }}
                            ></div>
                          </div>
                          <div className="text-xs text-right text-gray-500">
                            {state.selectedPatient.financialProfile.packageInfo.packageSessionsRemaining} sessões restantes
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Agendamentos */}
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-600 mb-1">Agendamentos</div>
                        {state.selectedPatient.appointmentSummary && (
                          <span className="text-xs text-gray-400">
                            {state.selectedPatient.appointmentSummary.periodType === 'package_cycle'
                              ? 'Período do pacote'
                              : 'Mês atual'}
                          </span>
                        )}
                      </div>
                      {state.selectedPatient.appointmentSummary ? (
                        <div className="space-y-1">
                          <div className="text-sm text-gray-900 font-semibold">
                            {state.selectedPatient.appointmentSummary.total} agendamento{state.selectedPatient.appointmentSummary.total === 1 ? '' : 's'} no período
                          </div>
                          {(() => {
                            const stats = [
                              { label: 'agendados', value: state.selectedPatient.appointmentSummary.scheduled, color: 'text-blue-600' },
                              { label: 'realizados', value: state.selectedPatient.appointmentSummary.completed, color: 'text-green-600' },
                              { label: 'cancelados', value: state.selectedPatient.appointmentSummary.cancelled, color: 'text-gray-600' },
                              { label: 'faltas', value: state.selectedPatient.appointmentSummary.noShow, color: 'text-red-600' }
                            ].filter(stat => stat.value > 0);

                            if (stats.length === 0) {
                              return (
                                <div className="text-xs text-gray-500">Nenhum agendamento no período selecionado.</div>
                              );
                            }

                            return (
                              <div className="flex flex-wrap gap-2 text-xs">
                                {stats.map(stat => (
                                  <span
                                    key={stat.label}
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 ${stat.color}`}
                                  >
                                    <span className="font-semibold">{stat.value}</span>
                                    <span>{stat.label}</span>
                                  </span>
                                ))}
                              </div>
                            );
                          })()}
                          <div className="text-xs text-gray-400">
                            {formatDate(state.selectedPatient.appointmentSummary.periodStart)} · {formatDate(state.selectedPatient.appointmentSummary.periodEnd)}
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">
                          Informações de agendamento indisponíveis.
                        </div>
                      )}
                    </div>

                    {/* Faturas */}
                    <div>
                      <div className="text-sm font-medium text-gray-600 mb-1">Faturas</div>
                      <div className="text-sm text-gray-500">
                        {state.selectedPatient.invoices.length} faturas registradas
                      </div>
                    </div>

                    {/* Botão Configurar */}
                    <button
                      onClick={() => {
                        if (state.selectedPatient) {
                          openFinancialModal(state.selectedPatient);
                        }
                      }}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Configurar Financeiro
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border shadow-sm p-8 text-center text-gray-500">
                  <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  Selecione um paciente para ver os detalhes
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Aba Faturas */}
      {activeTab === 'invoices' && (
        <InvoicesPanel
          patients={state.patients}
          initialDueMonth={requestedInvoicesDueMonth}
        />
      )}

      {/* Aba Repasse de Profissionais */}
      {activeTab === 'professionals' && (
        <ProfessionalPaymentPanel />
      )}

      {/* Modal Financeiro */}
      {showFinancialModal && state.selectedPatient && (
        <PatientFinancialModal
          patient={state.selectedPatient}
          isOpen={showFinancialModal}
          onClose={handleFinancialModalClose}
          onUpdate={handleFinancialModalUpdated}
        />
      )}
    </div>
  );
}
